import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend,
} from 'recharts';
import { Card } from '../../components/ui/Card';
import { RefreshCw, Tag, AlertTriangle, X, Package, TrendingDown, TrendingUp, ChevronRight, ChevronDown } from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ---------------------------------------------------------------------------
// Renk sistemi — doğrulanmış palet (dataviz skill, validate_palette.js).
//
// Isı haritası SIRALI (sequential): tek renk, açıktan koyuya. "İndirim yoğunluğu"
// büyüklük ölçtüğü için gökkuşağı değil tek hue.
// Alan grafiği KATEGORİK: 2 seri, paletin 1. ve 2. yuvası (mavi/turuncu),
// bitişik çift ΔE 24.7 (CVD) / 33.6 (normal) — ikisi de eşiğin çok üstünde.
// İndirim İKİ grafikte de MAVİ; renk seriyi değil ANLAMI takip ediyor.
// ---------------------------------------------------------------------------
const SEQ = {
  zero: { bg: '#f3f4f6', fg: '#374151' },      // 0 = indirim yok, yüzeye yakın nötr
  s100: { bg: '#cde2fb', fg: '#0d366b' },
  s200: { bg: '#9ec5f4', fg: '#0d366b' },
  s350: { bg: '#5598e7', fg: '#0d366b' },
  s450: { bg: '#2a78d6', fg: '#ffffff' },
  s600: { bg: '#184f95', fg: '#ffffff' },
};
const CAT_DISCOUNTED = '#2a78d6';   // yuva 1
const CAT_FULL_PRICE = '#eb6834';   // yuva 2

// Ölçüye göre ısı basamağı. Eşikler ölçü tipine göre değişiyor: oran %0-100,
// derinlik pratikte %0-50, adet ise markadan markaya 1-500 arasında.
function heatStep(value, metric) {
  if (value == null) return null;
  if (value <= 0) return SEQ.zero;
  if (metric === 'ratio') {
    if (value < 20) return SEQ.s100;
    if (value < 40) return SEQ.s200;
    if (value < 60) return SEQ.s350;
    if (value < 80) return SEQ.s450;
    return SEQ.s600;
  }
  // depth
  if (value < 10) return SEQ.s100;
  if (value < 20) return SEQ.s200;
  if (value < 30) return SEQ.s350;
  if (value < 40) return SEQ.s450;
  return SEQ.s600;
}

// Hücrede rakamlar (ürün / indirimli) HER ZAMAN yazılı; bu seçim sadece arka plan
// renginin neyi ölçtüğünü değiştiriyor. Eski "adet" modları rakam gelince gereksiz
// kaldı — üstelik satır-içi normalize edildikleri için markalar arası yanıltıyordu.
const METRICS = [
  { key: 'ratio', label: 'İndirimli oranı', unit: '%', hint: 'Renk: indirimli ürün / toplam ürün' },
  { key: 'depth', label: 'Ort. indirim', unit: '%', hint: 'Renk: indirimli ürünlerin ortalama indirim oranı' },
];

// ---------------------------------------------------------------------------
// Kategori kırılımı. Kovalar uçtan geliyor (`google_product_category`), burada
// yalnızca SIRA ve etiketler duruyor. İki kova katalogun tamamını böler —
// kategorisi olmayan ürün "Diğer"e düşer, hiçbir ürün kırılımdan düşmez.
// ---------------------------------------------------------------------------
const CATEGORY_ORDER = ['Ayakkabı', 'Diğer'];
const CAT_ALL = 'all';

// ---------------------------------------------------------------------------
// Küratörlü marka setleri — markaya özel, dışarıdan gelen bir listenin feed'de
// karşılığı var mı sorusunu yanıtlar. (Sunum/stand markaları, sezon seçkisi…)
//
// 🔑 Sunumdaki yazım feed'deki yazımla aynı olmak zorunda değil ("MELİ"→"Meli",
// "HUPALUPA Bubble Wands"→"Bubble Wand"), o yüzden eşleşme adayları ayrı
// tutuluyor ve harf duyarsız TAM eşleşmeyle aranıyor. Substring aramak "Meli"yi
// "Melissa&Doug"a bağlardı.
// 🔴 Eşleşmeyen marka listeden DÜŞMÜYOR, "feed'de yok" satırı olarak basılıyor —
// asıl bilgi zaten o: sunumda tanıtılan markanın katalogda karşılığı yok.
// ---------------------------------------------------------------------------
const CURATED_BRAND_SETS = {
  // HUPALUPA — Zorlu Center standı sunumunda tanıtılan markalar
  'cb6bbe2d-a3cd-4322-a4e4-2373d10225a9': {
    title: 'Sunum markaları — Zorlu Center standı',
    note: 'Sunumda tanıtılan markaların Merchant Center feed\'indeki karşılığı',
    items: [
      { label: 'PLAYMOBIL', match: ['playmobil'] },
      { label: 'NeeDoh', match: ['needoh', 'nee doh', 'nee-doh'] },
      { label: 'The Zoo Family', match: ['zoo family', 'the zoo family'] },
      { label: 'MODU', match: ['modu'] },
      { label: 'Learning Resources / Numberblocks', match: ['learning resources', 'numberblocks'] },
      { label: 'Doudou et Compagnie', match: ['doudou et compagnie', 'doudou'] },
      { label: 'Antonio Juan', match: ['antonio juan'] },
      { label: 'BAVVIC', match: ['bavvic'] },
      { label: 'HUPALUPA Bubble Wands', match: ['bubble wand', 'bubble wands'] },
      { label: 'Charm It', match: ['charm it', 'charm-it'] },
      { label: 'Clixo', match: ['clixo'] },
      { label: 'Loop Lasso', match: ['loop lasso'] },
      { label: 'MELİ', match: ['meli', 'melı', 'meli̇'] },
      { label: 'NICI', match: ['nici'] },
      { label: 'JarMelo', match: ['jarmelo', 'jar melo'] },
      { label: 'X-MUD', match: ['x-mud', 'xmud', 'x mud'] },
    ],
  },
};

// Aynı gün + aynı markanın kategori satırlarını tek satıra indirger.
// avg/max indirim ürün sayısıyla AĞIRLIKLI toplanır: 12 ürünlü "Ayakkabı"nın
// %40'ı ile 400 ürünlü "Diğer"in %10'u düz ortalanırsa marka %25 indirimde
// görünüyor, gerçek ağırlıklı değer %10,9.
function mergeCategoryRows(list) {
  if (!list.length) return null;
  const out = {
    date: list[0].date, brand: list[0].brand, category: null,
    total: 0, discounted: 0, outOfStock: 0,
    avgDiscount: null, maxDiscount: null, effectiveValue: 0, listValue: 0,
  };
  let avgNum = 0;
  for (const r of list) {
    out.total += r.total;
    out.discounted += r.discounted;
    out.outOfStock += r.outOfStock;
    if (r.avgDiscount != null) avgNum += r.avgDiscount * r.discounted;
    if (r.maxDiscount != null) out.maxDiscount = out.maxDiscount == null ? r.maxDiscount : Math.max(out.maxDiscount, r.maxDiscount);
    out.effectiveValue += r.effectiveValue;
    out.listValue += r.listValue;
  }
  out.avgDiscount = out.discounted > 0 ? Math.round((avgNum / out.discounted) * 10) / 10 : null;
  return out;
}

const fmtInt = (v) => (v == null ? '—' : Math.round(v).toLocaleString('tr-TR'));
const fmtPct1 = (v) => (v == null ? '—' : `%${v.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`);
const fmtMoney = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
const fmtDay = (d) => new Date(d + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
const fmtDayLong = (d) => new Date(d + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', weekday: 'long' });

export default function MerchantCenterDiscountTracker() {
  const { selectedBrand } = useAuth();
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState('ratio');
  const [category, setCategory] = useState(CAT_ALL);   // 'all' | 'Ayakkabı' | 'Diğer'
  const [expanded, setExpanded] = useState(() => new Set());  // matriste açık marka satırları
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drill, setDrill] = useState(null); // { date, brand, category }

  const fetchData = useCallback(async (opts = {}) => {
    if (!selectedBrand?.id) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ days: String(days) });
      if (opts.force) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/feed-discount/daily?${qs.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedBrand?.id, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Pivot: gün listesi (eskiden yeniye) + marka × gün matrisi -----------
  // 🔑 Uçtan gelen satırların taneciği gün × marka × KATEGORİ. Seçili kategoriye
  // göre süzülür, sonra marka düzeyine indirgenir; "Tümü"de kategoriler toplanır.
  // Marka sırası ve marka listesi de SÜZÜLMÜŞ veriden çıkarılıyor — yoksa
  // "Ayakkabı" seçiliyken katalogda hiç ayakkabısı olmayan markalar boş satır
  // olarak listede kalırdı.
  const { dayList, byBrand, byBrandCat, totalsByDay, brands } = useMemo(() => {
    const all = data?.rows || [];
    const rows = category === CAT_ALL ? all : all.filter(r => r.category === category);
    const dset = new Set();
    const cats = new Map();  // brand -> Map(category -> Map(date -> row))
    for (const r of rows) {
      dset.add(r.date);
      if (!cats.has(r.brand)) cats.set(r.brand, new Map());
      const cm = cats.get(r.brand);
      const key = r.category || 'Diğer';
      if (!cm.has(key)) cm.set(key, new Map());
      cm.get(key).set(r.date, r);
    }
    // Marka düzeyi: kategorileri ağırlıklı topla.
    const map = new Map(); // brand -> Map(date -> merged row)
    for (const [b, cm] of cats) {
      const dm = new Map();
      for (const [, dmap] of cm) {
        for (const [d, r] of dmap) {
          const prev = dm.get(d);
          dm.set(d, prev ? mergeCategoryRows([prev, r]) : { ...r, category: null });
        }
      }
      map.set(b, dm);
    }
    const dl = [...dset].sort();                 // soldan sağa zaman akışı
    const tot = dl.map(d => {
      let total = 0, discounted = 0, oos = 0, depthNum = 0;
      for (const r of rows) {
        if (r.date !== d) continue;
        total += r.total; discounted += r.discounted; oos += r.outOfStock;
        if (r.avgDiscount != null) depthNum += r.avgDiscount * r.discounted;
      }
      return {
        date: d,
        total,
        discounted,
        fullPrice: total - discounted,
        outOfStock: oos,
        ratio: total > 0 ? (discounted / total) * 100 : 0,
        depth: discounted > 0 ? depthNum / discounted : null,
      };
    });
    // Marka sırası: son günün ürün sayısı, sonra dönem toplamı (uçtakiyle aynı
    // kural, ama süzülmüş kapsam üzerinde).
    const last = dl.length ? dl[dl.length - 1] : null;
    const order = [...map.entries()].map(([b, dm]) => {
      let sum = 0;
      for (const r of dm.values()) sum += r.total;
      return { brand: b, latestTotal: (last && dm.get(last)?.total) || 0, sumTotal: sum };
    }).filter(x => x.sumTotal > 0)
      .sort((a, b) => (b.latestTotal - a.latestTotal) || (b.sumTotal - a.sumTotal))
      .map(x => x.brand);
    return { dayList: dl, byBrand: map, byBrandCat: cats, totalsByDay: tot, brands: order };
  }, [data, category]);

  // Matris satırları: marka + (açıksa) kategori alt satırları. Alt satır ancak
  // "Tümü" seçiliyken ve markanın gerçekten iki kovası varken anlamlı.
  const availableCategories = useMemo(
    () => (data?.categories || []).map(c => c.key).filter(k => CATEGORY_ORDER.includes(k)),
    [data]
  );
  const matrixRows = useMemo(() => {
    const out = [];
    for (const b of brands) {
      const cm = byBrandCat.get(b);
      const keys = cm ? CATEGORY_ORDER.filter(k => cm.has(k)) : [];
      const expandable = category === CAT_ALL && keys.length > 1;
      const isOpen = expandable && expanded.has(b);
      out.push({
        key: b, label: b, feedBrand: b, dmap: byBrand.get(b),
        expandable, expanded: isOpen,
        onToggle: () => setExpanded(prev => {
          const next = new Set(prev);
          if (next.has(b)) next.delete(b); else next.add(b);
          return next;
        }),
      });
      if (isOpen) {
        for (const k of keys) {
          out.push({
            key: `${b}|${k}`, label: k, tooltipLabel: `${b} · ${k}`,
            feedBrand: b, category: k, dmap: cm.get(k), indent: true,
          });
        }
      }
    }
    return out;
  }, [brands, byBrand, byBrandCat, category, expanded]);

  const latest = dayList.length ? dayList[dayList.length - 1] : null;
  const latestTotals = totalsByDay.length ? totalsByDay[totalsByDay.length - 1] : null;
  // Kıyas günü: 7 gün öncesi. Yoksa (kısa pencere) en eski gün.
  const prevTotals = useMemo(() => {
    if (!latest || totalsByDay.length < 2) return null;
    const target = new Date(latest + 'T00:00:00');
    target.setDate(target.getDate() - 7);
    const key = `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-${String(target.getDate()).padStart(2, '0')}`;
    return totalsByDay.find(t => t.date === key) || totalsByDay[0];
  }, [latest, totalsByDay]);

  // Dip toplam satırı gün bazında buradan okuyor: kolon toplamı satırların
  // gözle toplanmasıyla değil, KPI ve alan grafiğiyle AYNI kaynaktan geliyor.
  const totalsByDate = useMemo(
    () => new Map(totalsByDay.map(t => [t.date, t])),
    [totalsByDay]
  );

  const noData = !loading && data && data.hasData === false;

  const curatedSet = CURATED_BRAND_SETS[selectedBrand?.id] || null;

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* ---------------------------------------------------------------- */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl border border-blue-200 bg-blue-50 flex items-center justify-center">
                <Tag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Marka Bazlı Günlük İndirim Takibi</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Kaynak: Merchant Center feed'inin günlük anlık görüntüsü · Her gün bir kayıt ·
                  İndirimli = indirimli fiyat &lt; liste fiyatı
                </p>
                {category !== CAT_ALL && (
                  <p className="text-xs mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50 text-blue-800 font-medium" lang="tr">
                    Sayfanın tamamı <strong>{category}</strong> kategorisiyle sınırlı
                    <button type="button" onClick={() => setCategory(CAT_ALL)} className="underline underline-offset-2 hover:no-underline">
                      kaldır
                    </button>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Kategori seçicisi yalnızca feed'de iki kova da varsa çıkıyor:
                  oyuncak markasında "Ayakkabı" sekmesi göstermenin anlamı yok. */}
              {availableCategories.length > 1 && (
                <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl" lang="tr">
                  {[{ k: CAT_ALL, l: 'Tümü' }, ...availableCategories.map(k => ({ k, l: k }))].map(o => (
                    <button
                      key={o.k}
                      type="button"
                      onClick={() => setCategory(o.k)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        category === o.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              )}
              <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                {[30, 60, 90].map(o => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setDays(o)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      days === o ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {o} gün
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => fetchData({ force: true })}
                disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
            </div>
          </div>
        </div>

        {/* KPI şeridi — grafik değil, tek sayı; forma göre stat tile. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
          <StatTile
            label="Feed'deki ürün"
            value={fmtInt(latestTotals?.total)}
            delta={latestTotals && prevTotals ? latestTotals.total - prevTotals.total : null}
            deltaSuffix=" ürün"
            sub={latest ? `${fmtDayLong(latest)} itibarıyla` : '—'}
          />
          <StatTile
            label="İndirimli ürün"
            value={fmtInt(latestTotals?.discounted)}
            delta={latestTotals && prevTotals ? latestTotals.discounted - prevTotals.discounted : null}
            deltaSuffix=" ürün"
            sub={latestTotals ? `Katalogun ${fmtPct1(latestTotals.ratio)}'i` : '—'}
          />
          <StatTile
            label="Ortalama indirim"
            value={latestTotals?.depth == null ? '—' : fmtPct1(latestTotals.depth)}
            delta={latestTotals?.depth != null && prevTotals?.depth != null
              ? Math.round((latestTotals.depth - prevTotals.depth) * 10) / 10 : null}
            deltaSuffix=" puan"
            sub="Sadece indirimli ürünler üzerinden"
          />
          <StatTile
            label="Stok dışı"
            value={fmtInt(latestTotals?.outOfStock)}
            sub={data?.availabilitySince
              ? `Feed bu bilgiyi ${fmtDay(data.availabilitySince)}'tan beri taşıyor`
              : 'Feed stok bilgisi taşımıyor'}
            muted={!data?.availabilitySince}
          />
        </div>

        {/* Sessiz sıfır uyarısı: availability alanı sonradan gelmişse öncesindeki
            0'lar "tükenmedi" değil "ölçüm yok" demek. */}
        {data?.availabilitySince && dayList.length && data.availabilitySince > dayList[0] && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">
              Feed <strong>{fmtDay(data.availabilitySince)}</strong> tarihinden önce hiç "stok dışı" ürün
              taşımıyor. O tarihten öncesi için sıfır, "hiç ürün tükenmedi" değil <strong>ölçüm yok</strong> demek.
            </p>
          </div>
        )}

        {error && (
          <div className="px-6 py-4 text-sm text-red-600">Veri alınamadı: {error}</div>
        )}
        {noData && (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            {data?.note || 'Bu markada Merchant Center feed anlık görüntüsü yok.'}
          </div>
        )}
      </Card>

      {/* ---------------------------------------------------------------- */}
      {/* Katalog büyüklüğü + indirim payı. İki seri de AYNI birim (ürün adedi),
          bu yüzden tek eksende yığılı alan olarak çizilebiliyor — çift eksen yok. */}
      {!noData && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-800">Katalog büyüklüğü ve indirim payı</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Alanların toplamı feed'deki toplam ürün sayısı · Mavi alan indirimli ürünler
            </p>
          </div>
          <div className="px-4 py-5">
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={totalsByDay} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={fmtDay}
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                    minTickGap={28}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                    tickFormatter={(v) => v.toLocaleString('tr-TR')}
                  />
                  <RTooltip content={<AreaTooltip />} cursor={{ stroke: '#9ca3af', strokeWidth: 1 }} />
                  {/* Serilerin stroke'u beyaz (yığın ayıracı) olduğu için Recharts
                      efsaneyi de beyaz basıyor → görünmez oluyordu. Renk ve yazı
                      rengi burada açıkça veriliyor. */}
                  <Legend
                    verticalAlign="top"
                    height={28}
                    payload={[
                      { value: 'İndirimli', type: 'square', color: CAT_DISCOUNTED },
                      { value: 'Liste fiyatı', type: 'square', color: CAT_FULL_PRICE },
                    ]}
                    formatter={(v) => <span style={{ color: '#374151', fontSize: 12 }}>{v}</span>}
                  />
                  {/* stroke = yüzey rengi: yığılı alanlar arasında 2px ayırıcı boşluk */}
                  <Area
                    type="monotone" dataKey="discounted" stackId="1" name="İndirimli"
                    stroke="#ffffff" strokeWidth={2} fill={CAT_DISCOUNTED} fillOpacity={0.95}
                  />
                  <Area
                    type="monotone" dataKey="fullPrice" stackId="1" name="Liste fiyatı"
                    stroke="#ffffff" strokeWidth={2} fill={CAT_FULL_PRICE} fillOpacity={0.85}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Card>
      )}

      {/* ---------------------------------------------------------------- */}
      {!noData && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="font-semibold text-gray-800">Marka × Gün — ürün ve indirimli ürün sayısı</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Her hücrede <strong className="text-gray-700">üstte toplam ürün</strong>,
                <strong className="text-gray-700"> altta indirimli ürün</strong> sayısı ·
                Satırlar marka, kolonlar gün · Hücreye tıkla, o günün ürün listesi açılsın
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500">Renk</span>
              <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                {METRICS.map(m => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMetric(m.key)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      metric === m.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="px-6 py-4">
            <HeatLegend metric={metric} />
            <BrandDayMatrix
              rows={matrixRows}
              dayList={dayList}
              metric={metric}
              loading={loading}
              onCell={(date, brand, cat) => setDrill({ date, brand, category: cat || (category === CAT_ALL ? null : category) })}
              totalsByDate={totalsByDate}
              totalScope={category === CAT_ALL ? 'Tüm markalar' : `Tüm markalar · ${category}`}
            />
            <p className="mt-3 text-[11px] text-gray-400">
              Alt rakam yerine <strong>·</strong> = o gün hiç indirimli ürün yok ·
              Taralı hücre = marka o gün feed'de yok ·
              <strong> Toplam</strong> satırı o günün tüm markalarını kapsar ·
              31 günden uzun pencerede hücreye rakam sığmıyor, sadece renk kalıyor
              {availableCategories.length > 1 && category === CAT_ALL
                ? ' · Marka adının solundaki oka bas, satır Ayakkabı / Diğer olarak açılsın.'
                : '.'}
            </p>
          </div>
        </Card>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Küratörlü set (markaya özel). Genel marka özetinin ÜSTÜNDE duruyor:
          dışarıdan gelen bir listeyi doğrulamak için açılıyor, 240 markalık
          tablodan tek tek aranmasın diye. */}
      {!noData && latest && curatedSet && (
        <CuratedBrandTable
          config={curatedSet}
          brands={brands}
          byBrand={byBrand}
          dayList={dayList}
          loading={loading}
          latest={latest}
          baseDate={dayList[0]}
          onOpen={(brand) => setDrill({ date: latest, brand })}
          onCell={(date, brand) => setDrill({ date, brand })}
        />
      )}

      {/* ---------------------------------------------------------------- */}
      {!noData && latest && (
        <BrandSummaryTable
          brands={brands}
          byBrand={byBrand}
          byBrandCat={byBrandCat}
          categoryFilter={category}
          latest={latest}
          prevDate={prevTotals?.date}
          onOpen={(brand, cat) => setDrill({ date: latest, brand, category: cat || (category === CAT_ALL ? null : category) })}
        />
      )}

      {drill && (
        <ProductDrawer
          brandId={selectedBrand?.id}
          date={drill.date}
          itemBrand={drill.brand}
          category={drill.category}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function StatTile({ label, value, sub, delta, deltaSuffix = '', muted }) {
  const up = delta != null && delta > 0;
  const down = delta != null && delta < 0;
  return (
    <div className="px-6 py-4">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className={`text-2xl font-semibold tabular-nums ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{value}</div>
        {delta != null && delta !== 0 && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? 'text-emerald-600' : 'text-red-500'}`}>
            {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {up ? '+' : '−'}{Math.abs(delta).toLocaleString('tr-TR')}{deltaSuffix}
          </span>
        )}
        {down === false && up === false && delta === 0 && (
          <span className="text-xs text-gray-400">değişmedi</span>
        )}
      </div>
      <div className="mt-0.5 text-[11px] text-gray-400">{sub}</div>
    </div>
  );
}

// Crosshair + tooltip: alan grafiğinde varsayılan etkileşim katmanı.
function AreaTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const disc = payload.find(p => p.dataKey === 'discounted')?.value || 0;
  const full = payload.find(p => p.dataKey === 'fullPrice')?.value || 0;
  const total = disc + full;
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-gray-900 mb-1.5">{fmtDayLong(label)}</div>
      <Row color={CAT_DISCOUNTED} label="İndirimli" value={`${fmtInt(disc)} ürün`} />
      <Row color={CAT_FULL_PRICE} label="Liste fiyatı" value={`${fmtInt(full)} ürün`} />
      <div className="mt-1.5 pt-1.5 border-t border-gray-100 flex items-center justify-between gap-6 text-gray-600">
        <span>Toplam</span>
        <span className="font-semibold text-gray-900 tabular-nums">
          {fmtInt(total)} ürün · {fmtPct1(total > 0 ? (disc / total) * 100 : 0)} indirimli
        </span>
      </div>
    </div>
  );
}

function Row({ color, label, value }) {
  return (
    <div className="flex items-center justify-between gap-6 py-0.5">
      <span className="flex items-center gap-1.5 text-gray-600">
        <span className="w-2.5 h-2.5 rounded-[2px]" style={{ background: color }} />
        {label}
      </span>
      <span className="font-medium text-gray-900 tabular-nums">{value}</span>
    </div>
  );
}

function HeatLegend({ metric }) {
  const bands = metric === 'ratio'
    ? ['0', '<%20', '%20-40', '%40-60', '%60-80', '%80+']
    : ['0', '<%10', '%10-20', '%20-30', '%30-40', '%40+'];
  const steps = [SEQ.zero, SEQ.s100, SEQ.s200, SEQ.s350, SEQ.s450, SEQ.s600];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] text-gray-500">
        {metric === 'ratio' ? 'Arka plan: indirimli oranı' : 'Arka plan: ortalama indirim'}
      </span>
      <div className="flex items-center gap-[2px]">
        {steps.map((s, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-9 h-3.5 rounded-[2px]" style={{ background: s.bg }} />
            <span className="text-[9px] text-gray-400 mt-0.5">{bands[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Marka × gün ısı matrisi. Hem tüm markalar için hem küratörlü set için aynı
// bileşen kullanılıyor; fark yalnızca hangi satırların ve hangi dip toplamın
// verildiğinde.
//
// rows: [{ key, label, feedBrand, dmap }] — `feedBrand` null ise marka feed'de
// hiç yok, satırın TAMAMI taralı basılır (hücre hücre "veri yok"tan farklı bir
// şey söylüyor: marka hiç eşleşmemiş).
// İsteğe bağlı alanlar: `expandable`/`expanded`/`onToggle` marka satırını
// kategorilerine açar, `indent`+`category` ise o açılan alt satırdır — alt
// satırdan gelen tıklama çekmeceye kategoriyi de taşır.
// totalsByDate: Map(date -> { total, discounted, ratio, depth }) — dip toplam.
// labelWidth: küratörlü sette adlar uzun ("Learning Resources / Numberblocks"),
// 8.5rem'de kırpılıyorlar; satır sayısı az olduğu için yer ayırmak serbest.
function BrandDayMatrix({ rows, dayList, metric, loading, onCell, totalsByDate, totalScope, labelWidth = '8.5rem' }) {
  // Rakamlar iki satır halinde basılıyor; 31 günden fazlasında hücre genişliği
  // 3 haneli sayıyı taşımıyor, o zaman sadece renk kalıyor.
  const showCellText = dayList.length <= 31;
  // Ok için yer, yalnızca gerçekten açılabilir satır varsa ayrılıyor; küratörlü
  // sette (tek kova) marka adları 18px kaybetmesin.
  const anyExpandable = rows.some(r => r.expandable);
  const grid = { gridTemplateColumns: `${labelWidth} repeat(${dayList.length}, minmax(0, 1fr))` };
  const HATCH = 'repeating-linear-gradient(45deg,#fafafa,#fafafa 3px,#f0f0f0 3px,#f0f0f0 6px)';

  const cellValue = (row) => {
    if (!row) return null;
    if (metric === 'ratio') return row.total > 0 ? (row.discounted / row.total) * 100 : 0;
    return row.avgDiscount;
  };

  return (
    <>
      <div className="mt-3 space-y-[3px]">
        {/* Tarih başlığı */}
        <div className="grid gap-[2px] items-end" style={grid}>
          <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide pb-1">Marka</div>
          {dayList.map((d, i) => {
            // Etiket kalabalığı olmasın: kolon dar oldukça seyrelt.
            const every = dayList.length <= 31 ? 3 : dayList.length <= 62 ? 7 : 10;
            // Son gün her zaman etiketli; ama ondan hemen önceki periyodik
            // etiket çok yakınsa ikisi üst üste biniyordu ("9 Ağu10 Ağu").
            const isLast = i === dayList.length - 1;
            const show = isLast || (i % every === 0 && (dayList.length - 1 - i) >= Math.ceil(every / 2));
            return (
              <div key={d} className="text-[9px] text-gray-400 text-center leading-none pb-1 whitespace-nowrap overflow-visible">
                {show ? fmtDay(d) : ''}
              </div>
            );
          })}
        </div>

        {loading && <div className="py-10 text-center text-sm text-gray-400">Yükleniyor…</div>}

        {!loading && rows.map(r => (
          <div key={r.key} className="grid gap-[2px]" style={grid}>
            <div
              className={`text-xs truncate pr-2 flex items-center gap-1 ${
                r.indent ? 'text-gray-500' : r.feedBrand ? 'font-medium text-gray-700' : 'text-gray-400'
              }`}
              style={r.indent ? { paddingLeft: 18 } : undefined}
              title={r.feedBrand && r.feedBrand !== r.label ? `${r.label} (feed: ${r.feedBrand})` : (r.tooltipLabel || r.label)}
              lang="tr"
            >
              {/* Kategori açma oku sadece iki kovası olan markada; yer her satırda
                  ayrılıyor ki marka adları aynı hizada kalsın. */}
              {r.expandable ? (
                <button
                  type="button"
                  onClick={r.onToggle}
                  className="shrink-0 -ml-1 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700"
                  title={r.expanded ? 'Kategorileri kapat' : 'Kategorilere ayır'}
                >
                  {r.expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
              ) : (anyExpandable && !r.indent) ? <span className="w-[18px] shrink-0" /> : null}
              <span className="truncate">{r.label}</span>
            </div>
            {dayList.map(d => {
              const row = r.dmap?.get(d);
              const v = cellValue(row);
              // "Ölçü boş" ile "marka o gün feed'de yok" AYNI ŞEY DEĞİL:
              // hiç indirimi olmayan markada avgDiscount null geliyor, bu
              // hücre taralı (feed'de yok) basılırsa marka yokmuş gibi
              // okunuyordu. Satır varsa nötr sıfır kutusu göster.
              const step = v == null ? (row ? SEQ.zero : null) : heatStep(v, metric);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => row && onCell(d, r.feedBrand, r.category)}
                  title={
                    row
                      ? `${r.tooltipLabel || r.label} · ${fmtDayLong(d)}\n` +
                        `Ürün: ${fmtInt(row.total)}\n` +
                        `İndirimli: ${fmtInt(row.discounted)} (${fmtPct1(row.total > 0 ? (row.discounted / row.total) * 100 : 0)})\n` +
                        `Ort. indirim: ${row.avgDiscount == null ? 'indirimli ürün yok' : fmtPct1(row.avgDiscount)}` +
                        (row.maxDiscount == null ? '' : ` · En derin: ${fmtPct1(row.maxDiscount)}`)
                      : `${r.tooltipLabel || r.label} · ${fmtDayLong(d)}\n${r.feedBrand ? "Feed'de yok" : "Bu marka feed'de hiç geçmiyor"}`
                  }
                  className={`${showCellText ? 'h-[38px] py-1' : 'h-[26px]'} rounded-[3px] tabular-nums leading-none
                              flex flex-col items-center justify-center gap-[3px] transition-transform
                              ${row ? 'hover:ring-2 hover:ring-blue-900/40 cursor-pointer' : 'cursor-default'}`}
                  style={{
                    background: step ? step.bg : HATCH,
                    color: step ? step.fg : '#9ca3af',
                  }}
                >
                  {/* Üst rakam = feed'deki toplam ürün, alt rakam = indirimli.
                      Renk sadece arka planda; sayılar okumayı renge bırakmıyor. */}
                  {showCellText && row && (
                    <>
                      <span className="text-[10px] font-medium" style={{ opacity: 0.72 }}>
                        {fmtInt(row.total)}
                      </span>
                      <span className="text-[11px] font-bold">
                        {row.discounted > 0 ? fmtInt(row.discounted) : '·'}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        ))}

        {/* Dip toplam. Marka satırlarından kalın bir çizgiyle ayrılıyor,
            tıklanmıyor (çekmece tek marka istiyor). Renk ölçüsü satırlarla aynı;
            oran/ort. indirim burada da markaların ortalaması değil, kapsamın
            geneli üzerinden hesaplı. */}
        {!loading && dayList.length > 0 && (
          <div className="grid gap-[2px] pt-[6px] mt-[6px] border-t-2 border-gray-300" style={grid}>
            <div className="text-xs font-bold text-gray-900 uppercase tracking-wide pr-2 flex items-center">
              Toplam
            </div>
            {dayList.map(d => {
              const t = totalsByDate.get(d);
              const v = !t ? null : (metric === 'ratio' ? t.ratio : t.depth);
              const step = v == null ? (t ? SEQ.zero : null) : heatStep(v, metric);
              return (
                <div
                  key={d}
                  title={
                    t
                      ? `${totalScope} · ${fmtDayLong(d)}\n` +
                        `Ürün: ${fmtInt(t.total)}\n` +
                        `İndirimli: ${fmtInt(t.discounted)} (${fmtPct1(t.ratio)})\n` +
                        `Ort. indirim: ${t.depth == null ? 'indirimli ürün yok' : fmtPct1(t.depth)}`
                      : `${totalScope} · ${fmtDayLong(d)}\nVeri yok`
                  }
                  className={`${showCellText ? 'h-[38px] py-1' : 'h-[26px]'} rounded-[3px] tabular-nums leading-none
                              flex flex-col items-center justify-center gap-[3px] ring-1 ring-gray-900/10`}
                  style={{
                    background: step ? step.bg : HATCH,
                    color: step ? step.fg : '#9ca3af',
                  }}
                >
                  {showCellText && t && (
                    <>
                      <span className="text-[10px] font-semibold" style={{ opacity: 0.8 }}>
                        {fmtInt(t.total)}
                      </span>
                      <span className="text-[11px] font-bold">
                        {t.discounted > 0 ? fmtInt(t.discounted) : '·'}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Küratörlü marka tablosu: verilen liste × feed. Eşleşmeyen marka satırı
// silinmiyor, "feed'de yok" olarak kalıyor.
function CuratedBrandTable({ config, brands, byBrand, dayList, loading, latest, baseDate, onOpen, onCell }) {
  // Matrisin rengi genel matristen BAĞIMSIZ seçiliyor: bu kart kendi başına
  // okunan bir ek, üstteki seçimi takip etmesi beklenmiyor.
  const [metric, setMetric] = useState('ratio');

  const rows = useMemo(() => {
    // Kanonik feed adları harf duyarsız indekslenir; sunumdaki yazımla feed'in
    // yazımı büyük/küçük harfte ayrışıyor ("MELİ" ↔ "Meli").
    const index = new Map(brands.map(b => [b.toLowerCase(), b]));
    const out = config.items.map((item, i) => {
      const feedBrand = item.match.map(m => index.get(m)).find(Boolean) || null;
      const dmap = feedBrand ? byBrand.get(feedBrand) : null;
      const cur = dmap?.get(latest) || null;
      const base = baseDate ? dmap?.get(baseDate) || null : null;
      const total = cur?.total ?? 0;
      return {
        key: `${item.label}-${i}`,
        order: i,
        label: item.label,
        feedBrand,
        dmap,
        inFeed: !!cur && total > 0,
        total,
        discounted: cur?.discounted ?? 0,
        ratio: total > 0 ? ((cur?.discounted ?? 0) / total) * 100 : null,
        depth: cur?.avgDiscount ?? null,
        maxDepth: cur?.maxDiscount ?? null,
        // Ort. fiyat feed değerinden türetiliyor (liste fiyatı toplamı / ürün);
        // medyan feed anlık görüntüsünden gelmiyor, uçtan istenmiyor.
        avgPrice: total > 0 && cur?.listValue ? cur.listValue / total : null,
        effectiveValue: cur?.effectiveValue ?? 0,
        delta: cur && base ? cur.total - base.total : null,
        isNew: !!cur && !base,
      };
    });
    // Feed'dekiler büyükten küçüğe; olmayanlar dipte, sunumdaki sırayla.
    return out.sort((a, b) =>
      (Number(b.inFeed) - Number(a.inFeed)) || (b.total - a.total) || (a.order - b.order));
  }, [config, brands, byBrand, latest, baseDate]);

  const present = rows.filter(r => r.inFeed);
  const missing = rows.filter(r => !r.inFeed);
  const totals = present.reduce((a, r) => ({
    total: a.total + r.total,
    discounted: a.discounted + r.discounted,
    effectiveValue: a.effectiveValue + r.effectiveValue,
  }), { total: 0, discounted: 0, effectiveValue: 0 });

  // 🔴 Dip toplam SADECE bu setin markalarını kapsıyor. Sayfa genelindeki
  // totalsByDay tüm katalogu (240+ marka) sayıyor; onu buraya koymak "sunum
  // markaları" başlığı altında katalog toplamı göstermek olurdu.
  const curatedTotalsByDate = useMemo(() => {
    const m = new Map();
    for (const d of dayList) {
      let total = 0, discounted = 0, depthNum = 0, seen = false;
      for (const r of rows) {
        const row = r.dmap?.get(d);
        if (!row) continue;
        seen = true;
        total += row.total;
        discounted += row.discounted;
        if (row.avgDiscount != null) depthNum += row.avgDiscount * row.discounted;
      }
      if (seen) {
        m.set(d, {
          total,
          discounted,
          ratio: total > 0 ? (discounted / total) * 100 : 0,
          depth: discounted > 0 ? depthNum / discounted : null,
        });
      }
    }
    return m;
  }, [rows, dayList]);

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50/60 to-white">
        <h2 className="font-semibold text-gray-800">{config.title} — {fmtDayLong(latest)}</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {config.note} · Listedeki <strong className="text-gray-700">{rows.length}</strong> markadan{' '}
          <strong className="text-gray-700">{present.length}</strong> tanesi feed'de
          {missing.length > 0 && <> · <strong className="text-amber-700">{missing.length}</strong> tanesinin katalogda karşılığı yok</>}
        </p>
      </div>

      {missing.length > 0 && (
        <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>{missing.map(m => m.label).join(', ')}</strong> — bu markalar feed'de hiç geçmiyor.
            Sunumda tanıtılıyor ama Merchant Center kataloğunda ürünleri yok, dolayısıyla alışveriş
            reklamlarında da çıkmıyorlar.
          </p>
        </div>
      )}

      {/* Marka × gün matrisi — genel matrisin aynısı ama yalnızca bu setin
          satırları ve bu setin dip toplamı. Feed'de hiç olmayan markalar da
          satır olarak duruyor (tamamı taralı), listeden düşmüyorlar. */}
      {dayList.length > 0 && (
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">Marka × Gün — ürün ve indirimli ürün sayısı</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Her hücrede <strong className="text-gray-700">üstte toplam ürün</strong>,
                <strong className="text-gray-700"> altta indirimli ürün</strong> ·
                Hücreye tıkla, o günün ürün listesi açılsın
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500">Renk</span>
              <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                {METRICS.map(m => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMetric(m.key)}
                    className={`px-2.5 py-1 text-[11px] font-medium rounded-lg transition-colors ${
                      metric === m.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <HeatLegend metric={metric} />
          </div>
          <BrandDayMatrix
            rows={rows}
            dayList={dayList}
            metric={metric}
            loading={loading}
            onCell={onCell}
            totalsByDate={curatedTotalsByDate}
            totalScope="Sunum markaları"
            labelWidth="15rem"
          />
          <p className="mt-3 text-[11px] text-gray-400">
            Alt rakam yerine <strong>·</strong> = o gün hiç indirimli ürün yok ·
            Taralı hücre = marka o gün feed'de yok ·
            <strong> Toplam</strong> satırı yalnızca yukarıdaki {present.length} markayı kapsar,
            katalogun tamamını değil.
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          {/* lang="tr": CSS `uppercase` aksi halde "indirim"in i'sini noktasız
              I'ya çeviriyor ("ORT. INDIRIM"). */}
          <thead className="bg-gray-50" lang="tr">
            <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-2.5 text-left">Marka</th>
              <th className="px-3 py-2.5 text-right">Ürün</th>
              <th className="px-3 py-2.5 text-right">İndirimli</th>
              <th className="px-3 py-2.5 text-right">Oran</th>
              <th className="px-3 py-2.5 text-right">Ort. indirim</th>
              <th className="px-3 py-2.5 text-right">En derin</th>
              <th className="px-3 py-2.5 text-right">Ort. fiyat</th>
              <th className="px-3 py-2.5 text-right">Feed değeri</th>
              <th className="px-4 py-2.5 text-right">
                Δ ürün<span className="block normal-case tracking-normal text-[10px] text-gray-400">
                  {baseDate ? `${fmtDay(baseDate)}'a göre` : '—'}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr
                key={r.key}
                className={`border-t border-gray-100 ${
                  r.inFeed ? 'hover:bg-blue-50/40 cursor-pointer' : 'bg-gray-50/60'
                }`}
                onClick={() => r.inFeed && onOpen(r.feedBrand)}
              >
                <td className="px-4 py-2.5">
                  <span className={r.inFeed ? 'font-medium text-gray-800' : 'text-gray-400'} lang="tr">
                    {r.label}
                  </span>
                  {/* Yalnızca büyük/küçük harf farkı bilgi taşımıyor ("PLAYMOBIL"
                      → "feed: Playmobil"); gerçekten farklı yazımda göster. */}
                  {r.inFeed && r.feedBrand.toLowerCase() !== r.label.toLowerCase() && (
                    <span className="ml-2 text-[10px] text-gray-400">feed: {r.feedBrand}</span>
                  )}
                </td>
                {r.inFeed ? (
                  <>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmtInt(r.total)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                      {r.discounted > 0 ? fmtInt(r.discounted) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtPct1(r.ratio)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmtPct1(r.depth)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{fmtPct1(r.maxDepth)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">
                      {r.avgPrice == null ? '—' : fmtMoney(r.avgPrice)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmtMoney(r.effectiveValue)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {r.isNew
                        ? <span className="text-blue-600 font-medium">yeni</span>
                        : r.delta == null || r.delta === 0
                          ? <span className="text-gray-300">—</span>
                          : (
                            <span className={r.delta > 0 ? 'text-emerald-600 font-medium' : 'text-red-500 font-medium'}>
                              {r.delta > 0 ? '+' : '−'}{Math.abs(r.delta).toLocaleString('tr-TR')}
                            </span>
                          )}
                    </td>
                  </>
                ) : (
                  <td colSpan={8} className="px-3 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-medium">
                      <AlertTriangle className="w-3 h-3" />
                      Feed'de yok
                    </span>
                  </td>
                )}
              </tr>
            ))}
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td className="px-4 py-2.5 text-gray-900">TOPLAM ({present.length} marka)</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtInt(totals.total)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtInt(totals.discounted)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">
                {fmtPct1(totals.total > 0 ? (totals.discounted / totals.total) * 100 : null)}
              </td>
              <td colSpan={3} />
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtMoney(totals.effectiveValue)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="px-6 py-3 text-[11px] text-gray-400 border-t border-gray-100">
        Satıra tıkla → o markanın {fmtDayLong(latest)} ürün listesi açılır ·
        "Ort. fiyat" liste fiyatlarının ürün başına ortalaması · "Feed değeri" indirim sonrası toplam.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function BrandSummaryTable({ brands, byBrand, byBrandCat, categoryFilter, latest, prevDate, onOpen }) {
  // Marka satırı tıklanınca kategorilerine açılıyor. Alt satırlar marka satırıyla
  // AYNI hesaptan geçiyor (aşağıdaki `summarize`), yani toplamları marka satırını
  // birebir verir — ayrı bir uç çağrısı yok, veri zaten kategori taneciğinde.
  const [open, setOpen] = useState(() => new Set());

  const summarize = (cur, prev) => {
    const ratio = cur && cur.total > 0 ? (cur.discounted / cur.total) * 100 : null;
    const prevRatio = prev && prev.total > 0 ? (prev.discounted / prev.total) * 100 : null;
    return {
      total: cur?.total ?? 0,
      discounted: cur?.discounted ?? 0,
      ratio,
      ratioDelta: ratio != null && prevRatio != null ? ratio - prevRatio : null,
      depth: cur?.avgDiscount ?? null,
      maxDepth: cur?.maxDiscount ?? null,
      outOfStock: cur?.outOfStock ?? 0,
      listValue: cur?.listValue ?? 0,
      effectiveValue: cur?.effectiveValue ?? 0,
    };
  };

  const rows = brands.map(b => {
    const m = byBrand.get(b);
    const cm = byBrandCat?.get(b);
    const catKeys = cm ? CATEGORY_ORDER.filter(k => cm.has(k)) : [];
    return {
      brand: b,
      ...summarize(m?.get(latest), prevDate ? m?.get(prevDate) : null),
      // Kırılım sadece "Tümü" görünümünde ve iki kovası olan markada anlamlı.
      children: (categoryFilter === CAT_ALL && catKeys.length > 1)
        ? catKeys.map(k => ({
            category: k,
            ...summarize(cm.get(k).get(latest), prevDate ? cm.get(k).get(prevDate) : null),
          })).filter(c => c.total > 0)
        : [],
    };
  }).filter(r => r.total > 0);

  const totals = rows.reduce((a, r) => ({
    total: a.total + r.total,
    discounted: a.discounted + r.discounted,
    outOfStock: a.outOfStock + r.outOfStock,
    listValue: a.listValue + r.listValue,
    effectiveValue: a.effectiveValue + r.effectiveValue,
  }), { total: 0, discounted: 0, outOfStock: 0, listValue: 0, effectiveValue: 0 });

  return (
    <Card className="rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-800">Marka özeti — {fmtDayLong(latest)}</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Değişim sütunu 7 gün öncesine göre · "Feed değeri" liste fiyatlarının toplamı, indirim sonrası karşılığıyla
          {categoryFilter === CAT_ALL
            ? ' · Marka adının solundaki oka bas, satır Ayakkabı / Diğer olarak açılsın'
            : ` · Yalnızca ${categoryFilter}`}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-2.5 text-left">Marka</th>
              <th className="px-3 py-2.5 text-right">Ürün</th>
              <th className="px-3 py-2.5 text-right">İndirimli</th>
              <th className="px-3 py-2.5 text-right">Oran</th>
              <th className="px-3 py-2.5 text-right">7g değişim</th>
              <th className="px-3 py-2.5 text-right">Ort. indirim</th>
              <th className="px-3 py-2.5 text-right">En derin</th>
              <th className="px-3 py-2.5 text-right">Stok dışı</th>
              <th className="px-4 py-2.5 text-right">Feed değeri</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isOpen = open.has(r.brand);
              const toggle = (e) => {
                e.stopPropagation();   // satır tıklaması çekmeceyi açıyor, ok açmıyor
                setOpen(prev => {
                  const next = new Set(prev);
                  if (next.has(r.brand)) next.delete(r.brand); else next.add(r.brand);
                  return next;
                });
              };
              return (
              <React.Fragment key={r.brand}>
              <tr
                className="border-t border-gray-100 hover:bg-blue-50/40 cursor-pointer"
                onClick={() => onOpen(r.brand)}
              >
                <td className="px-4 py-2.5 font-medium text-gray-800">
                  <span className="flex items-center gap-1">
                    {r.children.length > 0 ? (
                      <button
                        type="button"
                        onClick={toggle}
                        className="shrink-0 -ml-1 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700"
                        title={isOpen ? 'Kategorileri kapat' : 'Kategorilere ayır'}
                      >
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    ) : <span className="w-[19px] shrink-0" />}
                    {r.brand}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmtInt(r.total)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmtInt(r.discounted)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">{fmtPct1(r.ratio)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {r.ratioDelta == null || Math.abs(r.ratioDelta) < 0.05
                    ? <span className="text-gray-300">—</span>
                    : (
                      <span className={r.ratioDelta > 0 ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                        {r.ratioDelta > 0 ? '+' : '−'}{Math.abs(r.ratioDelta).toFixed(1)} puan
                      </span>
                    )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmtPct1(r.depth)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{fmtPct1(r.maxDepth)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">
                  {r.outOfStock > 0 ? r.outOfStock : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                  <div>{fmtMoney(r.effectiveValue)}</div>
                  {r.listValue > r.effectiveValue && (
                    <div className="text-[10px] text-gray-400 line-through">{fmtMoney(r.listValue)}</div>
                  )}
                </td>
              </tr>
              {isOpen && r.children.map(c => (
                <tr
                  key={`${r.brand}|${c.category}`}
                  className="border-t border-gray-50 bg-gray-50/50 hover:bg-blue-50/40 cursor-pointer text-[13px]"
                  onClick={() => onOpen(r.brand, c.category)}
                >
                  <td className="px-4 py-2 text-gray-500" lang="tr">
                    <span className="pl-[27px] inline-flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-gray-300" />
                      {c.category}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtInt(c.total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtInt(c.discounted)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-700">{fmtPct1(c.ratio)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {c.ratioDelta == null || Math.abs(c.ratioDelta) < 0.05
                      ? <span className="text-gray-300">—</span>
                      : (
                        <span className={c.ratioDelta > 0 ? 'text-blue-600' : 'text-gray-500'}>
                          {c.ratioDelta > 0 ? '+' : '−'}{Math.abs(c.ratioDelta).toFixed(1)} puan
                        </span>
                      )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtPct1(c.depth)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-400">{fmtPct1(c.maxDepth)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-400">
                    {c.outOfStock > 0 ? c.outOfStock : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-600">{fmtMoney(c.effectiveValue)}</td>
                </tr>
              ))}
              </React.Fragment>
              );
            })}
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td className="px-4 py-2.5 text-gray-900">TOPLAM</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtInt(totals.total)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{fmtInt(totals.discounted)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">
                {fmtPct1(totals.total > 0 ? (totals.discounted / totals.total) * 100 : null)}
              </td>
              <td colSpan={3} />
              <td className="px-3 py-2.5 text-right tabular-nums">
                {totals.outOfStock > 0 ? totals.outOfStock : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(totals.effectiveValue)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function ProductDrawer({ brandId, date, itemBrand, category, onClose }) {
  const [products, setProducts] = useState(null);
  const [onlyDiscounted, setOnlyDiscounted] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!brandId) return;
      setLoading(true);
      try {
        const qs = new URLSearchParams({ date, brand: itemBrand });
        if (onlyDiscounted) qs.set('only', 'discounted');
        // Kategori süzgeci uçta /daily ile AYNI ifadeyle uygulanıyor; hücredeki
        // sayı ile buradaki liste böylece birebir tutuyor.
        if (category) qs.set('category', category);
        const r = await fetch(`${API_BASE}/api/feed-discount/products?${qs.toString()}`, {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId },
        });
        const j = r.ok ? await r.json() : { products: [] };
        if (!cancelled) setProducts(j.products || []);
      } catch (_) {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brandId, date, itemBrand, onlyDiscounted, category]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Opak yeterince koyu perde: arkadaki tablo okunmasın diye */}
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-600" />
              {itemBrand}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5" lang="tr">
              {fmtDayLong(date)} · feed anlık görüntüsü
              {category && <> · <span className="font-medium text-gray-700">{category}</span></>}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-5 py-2.5 border-b border-gray-100 flex items-center gap-2">
          {[{ k: true, l: 'Sadece indirimli' }, { k: false, l: 'Tümü' }].map(o => (
            <button
              key={String(o.k)}
              type="button"
              onClick={() => setOnlyDiscounted(o.k)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                onlyDiscounted === o.k ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {o.l}
            </button>
          ))}
          {products && <span className="text-xs text-gray-400 ml-auto">{products.length} ürün</span>}
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {loading && <div className="py-10 text-center text-sm text-gray-400">Yükleniyor…</div>}
          {!loading && products?.length === 0 && (
            <div className="py-10 text-center text-sm text-gray-400">Bu kırılımda ürün yok.</div>
          )}
          {!loading && products?.map(p => (
            <div key={p.offerId} className="flex items-center gap-3 px-5 py-3">
              {p.imageLink
                ? <img src={p.imageLink} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100 shrink-0" loading="lazy" />
                : <div className="w-12 h-12 rounded-lg bg-gray-100 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-800 truncate" title={p.title}>{p.title}</div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {p.offerId}{p.size ? ` · Beden ${p.size}` : ''}
                  {p.availability && p.availability !== 'in stock' && (
                    <span className="ml-1.5 text-amber-600 font-medium">· stok dışı</span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                {p.discountPct != null ? (
                  <>
                    <div className="text-xs font-semibold text-gray-900 tabular-nums">{fmtMoney(p.salePrice)}</div>
                    <div className="text-[10px] text-gray-400 line-through tabular-nums">{fmtMoney(p.price)}</div>
                    <div className="text-[10px] font-semibold" style={{ color: CAT_DISCOUNTED }}>−%{p.discountPct}</div>
                  </>
                ) : (
                  <div className="text-xs text-gray-700 tabular-nums">{fmtMoney(p.price)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
